/**
 * Fetch Interceptor for Prefetched Requests
 *
 * Wraps the global `fetch` to check the prefetch cache first.
 * If a matching prefetched response exists, it clones and returns it
 * instead of making a duplicate network request.
 *
 * This must be installed early — before CacheProvider / rest-hooks
 * makes any requests.
 */

import { consumePrefetch, initPrefetch } from "./prefetch";

let installed = false;

/**
 * Install the fetch interceptor that serves prefetched responses.
 *
 * Safe to call multiple times — will only install once.
 * Must be called before React renders any components that use useResource().
 */
export function installPrefetchInterceptor(): void {
	if (installed) return;
	installed = true;

	initPrefetch();

	const originalFetch = window.fetch;

	window.fetch = function prefetchAwareFetch(
		input: RequestInfo | URL,
		init?: RequestInit
	): Promise<Response> {
		// Extract the URL string
		let url: string;
		if (typeof input === "string") {
			url = input;
		} else if (input instanceof URL) {
			url = input.toString();
		} else if (input instanceof Request) {
			url = input.url;
		} else {
			return originalFetch.call(window, input, init);
		}

		// Check if we have a prefetched response for this URL.
		// The prefetched requests are POSTs with empty body — matching
		// the actual pattern used by rest-hooks/BasePlainResource.
		const prefetched = consumePrefetch(url);
		if (prefetched) {
			if (process.env.NODE_ENV !== "production") {
				console.log("[prefetch] Serving prefetched response for:", url);
			}
			// Clone the response so the original can't be consumed twice
			return prefetched.then(response => response.clone());
		}

		return originalFetch.call(window, input, init);
	};
}
